import {Form, Select} from 'antd';
import {rewardInstrumentKeys, rewardInstrumentKeyOptions} from 'utils/mock';

/**
 * `operationConfig.instrumentKeys` — the second, narrower half of a scheme's
 * asset scope.
 *
 * Independent of the Asset Class select: every instrument type is offered
 * whatever classes are chosen. The gate applies the two separately —
 * `assetClass` against the event's class, this against its instrument key — so
 * narrowing one does not imply the other, and the form does not pretend it
 * does.
 *
 * These are `InstrumentKey` members: the kind of instrument (listed stock, IPO,
 * bond…), never a particular security. The instrument a reward pays *out* is
 * chosen separately, by id, in the reward configuration.
 *
 * Optional on its own — left empty, every instrument type qualifies. What an
 * investment-scoped scheme cannot do is leave this *and* Asset Class empty:
 * that describes no scope at all, so the Asset Class field carries a rule
 * requiring one of the two.
 */
export const InstrumentKeysField = ({disabled}) => (
  <Form.Item
    label="Instrument Types"
    name="instrumentKeys"
    tooltip="Which kinds of instrument qualify — the type, not a particular security. Any type can be chosen, whatever the asset classes above. Leave empty to include every type.">
    <Select
      mode="multiple"
      allowClear
      disabled={disabled}
      placeholder="All instrument types">
      {rewardInstrumentKeyOptions.map(({key, name}) => (
        <Select.Option value={key} key={key}>
          {name}
        </Select.Option>
      ))}
    </Select>
  </Form.Item>
);

/**
 * The asset class a deprecated `RewardAssetClass` value now reads as.
 *
 * Rewards issued before the split stored an instrument key (`IPO`, `BOND`, …)
 * in `eventAssetClass`; reopening one has to put that value back into the two
 * selects that replaced it rather than leaving both blank.
 */
export const assetClassForInstrumentKey = instrumentKey =>
  Object.keys(rewardInstrumentKeys).find(assetClass =>
    rewardInstrumentKeys[assetClass].some(({key}) => key === instrumentKey),
  );
